import axios, { AxiosResponse } from 'axios';
import {
  Feedback,
  FeedbackCategory,
  HospitalLocation,
  User,
  AuthResponse,
  LoginDto,
  RegisterDto,
  ChangePasswordDto,
  FeedbackFormDto,
  FeedbackUpdateDto,
  PaginatedResponse,
  QueryParams,
  ApiResponse,
  HierarchicalLocation,
  CreateDepartmentDto,
  CreateFloorDto,
  CreateRoomDto,
  BulkCreateRoomsDto,
  BulkCreateRoomsRangeDto,
  BatchQRResponse
} from '../types';

const API_BASE_URL = 'http://172.173.131.89:86/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: LoginDto): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login', data),

  register: (data: RegisterDto): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/register', data),

  getCurrentUser: (): Promise<AxiosResponse<User>> =>
    api.get('/auth/me'),

  changePassword: (data: ChangePasswordDto): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/change-password', data),

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
};

// Feedback API
export const feedbackAPI = {
  // Get all feedbacks with filtering and pagination
  getFeedbacks: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Feedback>>> => {
    return api.get('/feedback', { params });
  },

  // Get feedback by ID
  getFeedback: (id: number): Promise<AxiosResponse<Feedback>> =>
    api.get(`/feedback/${id}`),

  // Create new feedback (public endpoint)
  createFeedback: (data: FormData): Promise<AxiosResponse<ApiResponse<Feedback>>> =>
    api.post('/feedback', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Update feedback (admin only)
  updateFeedback: (id: number, data: FeedbackUpdateDto): Promise<AxiosResponse<ApiResponse<Feedback>>> =>
    api.put(`/feedback/${id}`, data),

  // Delete feedback (admin only)
  deleteFeedback: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/feedback/${id}`),

  // Get feedback statistics
  getStatistics: (): Promise<AxiosResponse<any>> =>
    api.get('/feedback/statistics'),

  // Export feedbacks
  exportFeedbacks: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/feedback/export', {
      params,
      responseType: 'blob'
    })
};

// Categories API
export const categoriesAPI = {
  // Get all categories
  getCategories: (): Promise<AxiosResponse<FeedbackCategory[]>> =>
    api.get('/categories'),

  // Get category by ID
  getCategory: (id: number): Promise<AxiosResponse<FeedbackCategory>> =>
    api.get(`/categories/${id}`),

  // Create category (admin only)
  createCategory: (data: Partial<FeedbackCategory>): Promise<AxiosResponse<FeedbackCategory>> =>
    api.post('/categories', data),

  // Update category (admin only)
  updateCategory: (id: number, data: Partial<FeedbackCategory>): Promise<AxiosResponse<FeedbackCategory>> =>
    api.put(`/categories/${id}`, data),

  // Delete category (admin only)
  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/categories/${id}`)
};

// Locations API
export const locationsAPI = {
  // Get all locations
  getLocations: (): Promise<AxiosResponse<HospitalLocation[]>> =>
    api.get('/locations'),

  // Get location by ID
  getLocation: (id: number): Promise<AxiosResponse<HospitalLocation>> =>
    api.get(`/locations/${id}`),

  // Create location (admin only)
  createLocation: (data: Partial<HospitalLocation>): Promise<AxiosResponse<HospitalLocation>> =>
    api.post('/locations', data),

  // Update location (admin only)
  updateLocation: (id: number, data: Partial<HospitalLocation>): Promise<AxiosResponse<HospitalLocation>> =>
    api.put(`/locations/${id}`, data),

  // Delete location (admin only)
  deleteLocation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/locations/${id}`),

  // Get all locations for QR code generation
  getLocationsForQR: (): Promise<AxiosResponse<any[]>> =>
    api.get('/locations/all-for-qr'),

  // Create custom QR location (SuperAdmin only)
  createCustomQR: (locationName: string): Promise<AxiosResponse<any>> =>
    api.post('/locations/custom-qr', { locationName }),

  // Delete custom QR location (SuperAdmin only)
  deleteCustomQR: (id: number): Promise<AxiosResponse<any>> =>
    api.delete(`/locations/custom-qr/${id}`)
};

// Hierarchical Locations API
export const hierarchicalLocationsAPI = {
  // Get full hierarchical tree
  getHierarchy: (): Promise<AxiosResponse<HierarchicalLocation[]>> =>
    api.get('/locations/hierarchy'),

  // Create new department
  createDepartment: (data: CreateDepartmentDto): Promise<AxiosResponse<HierarchicalLocation>> =>
    api.post('/locations/department', data),

  // Create new floor under department
  createFloor: (data: CreateFloorDto): Promise<AxiosResponse<HierarchicalLocation>> =>
    api.post('/locations/floor', data),

  // Create single room under floor
  createRoom: (data: CreateRoomDto): Promise<AxiosResponse<HierarchicalLocation>> =>
    api.post('/locations/room', data),

  // Bulk create rooms
  bulkCreateRooms: (data: BulkCreateRoomsDto): Promise<AxiosResponse<HierarchicalLocation[]>> =>
    api.post('/locations/rooms/bulk', data),

  // Bulk create rooms with range
  bulkCreateRoomsRange: (data: BulkCreateRoomsRangeDto): Promise<AxiosResponse<HierarchicalLocation[]>> =>
    api.post('/locations/rooms/bulk-range', data),

  // Get floors for department
  getDepartmentFloors: (departmentId: number): Promise<AxiosResponse<HierarchicalLocation[]>> =>
    api.get(`/locations/department/${departmentId}/floors`),

  // Get rooms for floor
  getFloorRooms: (floorId: number): Promise<AxiosResponse<HierarchicalLocation[]>> =>
    api.get(`/locations/floor/${floorId}/rooms`),

  // Get all QR codes for a floor (batch)
  getFloorQRBatch: (floorId: number): Promise<AxiosResponse<BatchQRResponse>> =>
    api.get(`/locations/qr-batch/${floorId}`)
};

// Admin API
export const adminAPI = {
  // Get all users
  getUsers: (params?: any): Promise<AxiosResponse<PaginatedResponse<User>>> =>
    api.get('/admin/users', { params }),

  // Get user by ID
  getUser: (id: string): Promise<AxiosResponse<User>> =>
    api.get(`/admin/users/${id}`),

  // Update user
  updateUser: (id: string, data: any): Promise<AxiosResponse<User>> =>
    api.put(`/admin/users/${id}`, data),

  // Delete user
  deleteUser: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/users/${id}`),

  // Assign user to category
  assignUserToCategory: (userId: string, categoryId: number, permissions: any): Promise<AxiosResponse<void>> =>
    api.post(`/admin/users/${userId}/categories/${categoryId}`, permissions),

  // Remove user from category
  removeUserFromCategory: (userId: string, categoryId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/users/${userId}/categories/${categoryId}`),

  // Get dashboard statistics
  getDashboardStats: (): Promise<AxiosResponse<any>> =>
    api.get('/admin/dashboard')
};

export default api;

// User Management API
export const userManagementAPI = {
  // Get all users
  getAllUsers: (): Promise<AxiosResponse<any[]>> =>
    api.get('/usermanagement/users'),

  // Get available roles
  getAvailableRoles: (): Promise<AxiosResponse<any[]>> =>
    api.get('/usermanagement/available-roles'),

  // Create new user
  createUser: (data: any): Promise<AxiosResponse<any>> =>
    api.post('/usermanagement/create-user', data),

  // Update user roles
  updateUserRoles: (userId: string, data: any): Promise<AxiosResponse<any>> =>
    api.put(`/usermanagement/update-user-roles/${userId}`, data),

  // Delete user
  deleteUser: (userId: string): Promise<AxiosResponse<any>> =>
    api.delete(`/usermanagement/delete-user/${userId}`)
};